import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Collapse,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Schedule as ScheduleIcon } from '@mui/icons-material';
import { CARD_BORDER_RADIUS } from '../types';
import { Silence } from '../../../../../api/cansense/silenceApi';
import { formatTimeRemaining } from '../components/SilenceCard';

// ============================================================================
// Types
// ============================================================================

export interface ExtendSilenceDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (id: number, durationMinutes: number) => Promise<void>;
  silence: Silence | null;
  loading?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const durationPresets = [
  { label: '30 min', value: 30 },
  { label: '1 hr', value: 60 },
  { label: '4 hrs', value: 240 },
  { label: '24 hrs', value: 1440 },
  { label: 'Custom', value: -1 },
];

// ============================================================================
// Component
// ============================================================================

/**
 * ExtendSilenceDialog - Dialog for extending an existing silence duration.
 *
 * Features:
 * - Shows current silence info (target, time remaining)
 * - Duration presets (30min, 1hr, 4hrs, 24hrs, custom)
 * - Custom duration input
 */
export function ExtendSilenceDialog({
  open,
  onClose,
  onSubmit,
  silence,
  loading = false,
}: ExtendSilenceDialogProps) {
  const [selectedDuration, setSelectedDuration] = useState<number>(60);
  const [customHours, setCustomHours] = useState<string>('');
  const [customMinutes, setCustomMinutes] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [longDurationConfirmed, setLongDurationConfirmed] = useState<boolean>(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedDuration(60);
      setCustomHours('');
      setCustomMinutes('');
      setError('');
      setLongDurationConfirmed(false);
    }
  }, [open]);

  // Reset long duration warning when duration changes
  const handleDurationChange = (value: number) => {
    setSelectedDuration(value);
    setLongDurationConfirmed(false);
    setError('');
  };

  const showCustomFields = selectedDuration === -1;

  const handleExtend = async () => {
    if (!silence) return;
    setError('');

    // Calculate final duration in minutes
    let durationMinutes = selectedDuration;
    if (selectedDuration === -1) {
      const hours = parseInt(customHours, 10) || 0;
      const minutes = parseInt(customMinutes, 10) || 0;
      durationMinutes = hours * 60 + minutes;
    }

    if (durationMinutes <= 0) {
      setError('Duration must be greater than zero');
      return;
    }

    // Warn for very long durations (> 7 days) but don't block
    const MAX_WARN_MINUTES = 7 * 24 * 60; // 7 days
    if (durationMinutes > MAX_WARN_MINUTES && !longDurationConfirmed) {
      const days = Math.floor(durationMinutes / (24 * 60));
      setError(`Warning: This will extend the silence by ${days} days. Click "Extend Silence" again to confirm.`);
      setLongDurationConfirmed(true);
      return;
    }

    try {
      await onSubmit(silence.id, durationMinutes);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to extend silence');
    }
  };

  // Validation
  const isValid =
    selectedDuration > 0 ||
    (selectedDuration === -1 &&
      (parseInt(customHours, 10) > 0 || parseInt(customMinutes, 10) > 0));

  // Calculate current time remaining
  const currentRemaining = silence
    ? new Date(silence.endsAt).getTime() - Date.now()
    : 0;

  // Get display target
  const displayTarget = silence
    ? silence.exporterName ||
      silence.matchers.map((m) => `${m.name}="${m.value}"`).join(', ') ||
      'All alerts'
    : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: CARD_BORDER_RADIUS },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScheduleIcon color="primary" />
        Extend Silence
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* Current Silence Info */}
          {silence && (
            <Box
              sx={{
                p: 2,
                mb: 3,
                bgcolor: 'grey.50',
                borderRadius: CARD_BORDER_RADIUS,
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Silenced target:
              </Typography>
              <Typography variant="subtitle2" fontWeight={600}>
                {displayTarget}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Current time remaining:{' '}
                <strong>
                  {currentRemaining > 0 ? formatTimeRemaining(currentRemaining) : 'Expired'}
                </strong>
              </Typography>
            </Box>
          )}

          {/* Duration */}
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Extend by:
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: showCustomFields ? 2 : 0 }}>
            {durationPresets.map((preset) => (
              <Button
                key={preset.value}
                variant={selectedDuration === preset.value ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleDurationChange(preset.value)}
                sx={{
                  textTransform: 'none',
                  borderRadius: CARD_BORDER_RADIUS,
                  minWidth: 70,
                }}
              >
                {preset.label}
              </Button>
            ))}
          </Box>

          {/* Custom Duration Fields */}
          <Collapse in={showCustomFields}>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                size="small"
                label="Hours"
                type="number"
                value={customHours}
                onChange={(e) => setCustomHours(e.target.value)}
                inputProps={{ min: 0, max: 168 }}
                sx={{
                  width: 100,
                  '& .MuiOutlinedInput-root': { borderRadius: CARD_BORDER_RADIUS },
                }}
              />
              <TextField
                size="small"
                label="Minutes"
                type="number"
                value={customMinutes}
                onChange={(e) => setCustomMinutes(e.target.value)}
                inputProps={{ min: 0, max: 59 }}
                sx={{
                  width: 100,
                  '& .MuiOutlinedInput-root': { borderRadius: CARD_BORDER_RADIUS },
                }}
              />
            </Box>
          </Collapse>

          {error && (
            <Alert severity={longDurationConfirmed ? 'warning' : 'error'} sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExtend}
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <ScheduleIcon />}
          sx={{ textTransform: 'none' }}
        >
          {loading ? 'Extending...' : 'Extend Silence'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ExtendSilenceDialog;
